/**
 * Apply rates from a previous study (contracts-plan.md §8.4, WP-53; §11 tab
 * 4). Study -> its negotiations' EXECUTED versions -> the four parts as
 * checkboxes -> the whole-budget toggle -> Apply. After the first post, a
 * per-line override table from `matched` (three-way select per line) —
 * re-posting with `lineOverrides` and the SAME `batchId` groups every
 * proposal from this apply as one act ("accept this apply", §8.3's own
 * pattern reused here).
 *
 * POST /workspaces/:id/apply/previous-study is mounted, same commit as
 * apply/rate-card (2bf341e3, APPLY-ROUTES; api/workspace.ts's header).
 * `onError` still surfaces any failure loudly (G13): a toast in the parent
 * plus this dialog's own inline `ws-apply-previous-study-error`.
 */
import { useEffect, useState } from 'react';
import { Modal, FilterSelect, SegmentedControl, primaryBtnCls, secondaryBtnCls, fieldCls } from '../../../ui';
import { listStudies, listNegotiations } from '../../api/client';
import type { Study, Negotiation } from '../../api/client';
import {
  applyPreviousStudy,
  listNegotiationVersions,
  describeWorkspaceApiError,
  type ApplyPreviousStudyParts,
  type ApplyPreviousStudyResult,
  type ContractsVersion,
  type PreviousStudyLineSource,
  type PreviousStudyWholeBudgetSource,
} from '../../api/workspace';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  onApplied: (result: ApplyPreviousStudyResult) => void;
  onError: (message: string) => void;
}

const PART_LABELS: Record<keyof ApplyPreviousStudyParts, string> = {
  procedures: 'Procedures',
  fees: 'Fees',
  overhead: 'Overhead percentage',
  holdback: 'Holdback terms',
};

const LINE_SOURCE_OPTIONS: Array<{ value: PreviousStudyLineSource; label: string }> = [
  { value: 'previous_study', label: 'Previous study' },
  { value: 'rate_card', label: 'Rate card' },
  { value: 'skip', label: 'Skip' },
];

export function ApplyPreviousStudyDialog({ isOpen, onClose, workspaceId, onApplied, onError }: Props) {
  const [studies, setStudies] = useState<Study[]>([]);
  const [studyId, setStudyId] = useState('');
  const [negotiations, setNegotiations] = useState<Negotiation[]>([]);
  const [negotiationId, setNegotiationId] = useState('');
  const [versions, setVersions] = useState<ContractsVersion[]>([]);
  const [versionId, setVersionId] = useState('');
  const [parts, setParts] = useState<ApplyPreviousStudyParts>({ procedures: false, fees: false, overhead: false, holdback: false });
  const [wholeBudgetSource, setWholeBudgetSource] = useState<PreviousStudyWholeBudgetSource>('previous_study');
  const [response, setResponse] = useState<ApplyPreviousStudyResult | null>(null);
  const [overrides, setOverrides] = useState<Record<string, PreviousStudyLineSource>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    listStudies().then(setStudies).catch(() => setStudies([]));
  }, [isOpen]);

  useEffect(() => {
    if (!studyId) {
      setNegotiations([]);
      return;
    }
    listNegotiations(studyId).then(setNegotiations).catch(() => setNegotiations([]));
  }, [studyId]);

  useEffect(() => {
    if (!negotiationId) {
      setVersions([]);
      return;
    }
    listNegotiationVersions(negotiationId)
      .then((rows) => setVersions(rows.filter((v) => v.is_executed)))
      .catch(() => setVersions([]));
  }, [negotiationId]);

  if (!isOpen) return null;

  const path = `/api/contracts/workspaces/${workspaceId}/apply/previous-study`;
  const anyPartSelected = Object.values(parts).some(Boolean);

  async function submit(withOverrides: boolean) {
    if (!studyId || !versionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await applyPreviousStudy(workspaceId, {
        sourceStudyId: studyId,
        sourceVersionId: versionId,
        parts,
        wholeBudgetSource,
        lineOverrides: withOverrides ? overrides : undefined,
        batchId: response?.batchId,
      });
      setResponse(result);
      const nextOverrides: Record<string, PreviousStudyLineSource> = {};
      for (const line of result.matched) nextOverrides[line.line_id] = line.source;
      setOverrides(nextOverrides);
      onApplied(result);
    } catch (err) {
      const message = describeWorkspaceApiError('POST', path, err);
      setError(message);
      onError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function close() {
    setResponse(null);
    setOverrides({});
    setError(null);
    onClose();
  }

  return (
    <Modal isOpen={isOpen} onClose={close} title="Apply previous study" maxWidth="max-w-2xl">
      <div data-testid="ws-apply-previous-study-dialog" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            label="Previous study"
            auto
            value={studyId}
            onChange={(v) => {
              setStudyId(v);
              setNegotiationId('');
              setVersionId('');
            }}
            options={[{ value: '', label: 'Choose a study…' }, ...studies.map((s) => ({ value: s.id, label: s.protocol_number }))]}
          />
          <FilterSelect
            label="Negotiation"
            auto
            value={negotiationId}
            disabled={!studyId}
            onChange={(v) => {
              setNegotiationId(v);
              setVersionId('');
            }}
            options={[{ value: '', label: 'Choose a negotiation…' }, ...negotiations.map((n) => ({ value: n.id, label: n.label ?? n.kind }))]}
          />
          <FilterSelect
            label="Executed version"
            auto
            value={versionId}
            disabled={!negotiationId}
            onChange={setVersionId}
            options={[{ value: '', label: 'Choose an executed version…' }, ...versions.map((v) => ({ value: v.id, label: `#${v.version_no} — ${v.version_date}` }))]}
          />
        </div>

        <fieldset data-testid="ws-aps-parts" className="space-y-1">
          <legend className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">Parts to copy (§8.4)</legend>
          {(Object.keys(PART_LABELS) as Array<keyof ApplyPreviousStudyParts>).map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                data-testid={`ws-aps-part-${key}`}
                checked={parts[key] === true}
                onChange={(e) => setParts((p) => ({ ...p, [key]: e.target.checked }))}
              />
              {PART_LABELS[key]}
            </label>
          ))}
        </fieldset>

        <div>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Whole-budget source
          </span>
          <SegmentedControl
            label="Whole-budget source"
            value={wholeBudgetSource}
            onChange={(v) => setWholeBudgetSource(v as PreviousStudyWholeBudgetSource)}
            options={[
              { value: 'previous_study', label: 'Previous study' },
              { value: 'rate_card', label: 'Rate card Preferred' },
            ]}
          />
        </div>

        {error && (
          <p data-testid="ws-apply-previous-study-error" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" className={secondaryBtnCls} onClick={close}>
            Close
          </button>
          <button
            type="button"
            data-testid="ws-apply-previous-study-submit"
            className={primaryBtnCls}
            disabled={submitting || !studyId || !versionId || !anyPartSelected}
            onClick={() => submit(false)}
          >
            Apply
          </button>
        </div>

        {response && (
          <div data-testid="ws-aps-overrides" className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700">
            <div data-testid="ws-aps-parts-applied" className="space-y-1 rounded-lg border border-gray-200 p-2.5 dark:border-gray-700">
              <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Per-part receipt (§8.4)
              </h4>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="pr-2 font-medium">Part</th>
                    <th className="pr-2 font-medium">Selected</th>
                    <th className="pr-2 font-medium">Matched</th>
                    <th className="pr-2 font-medium">Proposed</th>
                    <th className="pr-2 font-medium">Skipped</th>
                    <th className="pr-2 font-medium">Unmatched</th>
                    <th className="font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(Object.keys(PART_LABELS) as Array<keyof ApplyPreviousStudyParts>).map((partKey) => {
                    const part = response.partsApplied[partKey];
                    const hasValueDetail = (partKey === 'overhead' || partKey === 'holdback') && part.value !== null && part.value !== undefined;
                    return (
                      <tr key={partKey} data-testid={`ws-aps-part-applied-${partKey}`}>
                        <td className="py-1 pr-2 text-gray-700 dark:text-gray-300">{PART_LABELS[partKey]}</td>
                        <td className="py-1 pr-2 tabular-nums">{part.selected ? 'yes' : 'no'}</td>
                        <td className="py-1 pr-2 tabular-nums">{part.matched ?? '—'}</td>
                        <td className="py-1 pr-2 tabular-nums">{part.proposed}</td>
                        <td className="py-1 pr-2 tabular-nums">{part.skipped ?? '—'}</td>
                        <td className="py-1 pr-2 tabular-nums">{part.unmatched ?? '—'}</td>
                        <td className="py-1 text-gray-500 dark:text-gray-400">
                          {hasValueDetail
                            ? `${part.value} → ${part.sheet ?? '—'}!${part.cell_ref ?? '—'}`
                            : (part.reason ?? '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
              Per-line source ({response.matched.length} matched)
            </h4>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {response.matched.map((line) => (
                    <tr key={line.line_id}>
                      <td className="py-1.5 pr-2 text-gray-700 dark:text-gray-300">{line.label_raw}</td>
                      <td className="py-1.5 text-gray-500 dark:text-gray-400">{line.value ?? '—'}</td>
                      <td className="py-1.5">
                        <select
                          data-testid={`ws-aps-override-${line.line_id}`}
                          className={fieldCls}
                          value={overrides[line.line_id] ?? line.source}
                          onChange={(e) =>
                            setOverrides((prev) => ({ ...prev, [line.line_id]: e.target.value as PreviousStudyLineSource }))
                          }
                        >
                          {LINE_SOURCE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {response.unmatched.length > 0 && (
              <div data-testid="ws-aps-unmatched">
                <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Unmatched ({response.unmatched.length})
                </h4>
                <ul className="mt-1 list-inside list-disc text-xs text-gray-600 dark:text-gray-300">
                  {response.unmatched.map((u) => (
                    <li key={u.line_id}>
                      {u.label_raw} — {u.section}/{u.part}: {u.reason}
                      {u.candidates.length > 0 && (
                        <span className="text-gray-400 dark:text-gray-500">
                          {' '}
                          (candidates: {u.candidates.map((c) => `${c.official_name} (${c.score})`).join(', ')})
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                data-testid="ws-apply-previous-study-reapply"
                className={primaryBtnCls}
                disabled={submitting}
                onClick={() => submit(true)}
              >
                Re-apply with overrides
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ApplyPreviousStudyDialog;
